import { debounce } from '../../../static/template-gispen/js/utils/debounce';

class Parallax {
  _state = {
    inViewport: false,
    imageSize: null,
    width: null,
    isVideo: false,
  };

  static options = {
    speed: 0.1,
  };

  constructor(element, options) {
    this._element = element;
    this._options = { options, ...Parallax.options };
    this._cacheSelectors();
    this._initialize();
  }

  _cacheSelectors() {
    this._elements = {
      image: this._element.querySelector('.parallax__image'),
      video: this._element.querySelector('.parallax__video'),
    };
  }

  _initialize() {
    const debounceTime = 300;

    this._state.isVideo = Boolean(this._elements.video);

    window.addEventListener(
      'resize',
      debounce(() => {
        this._resize();
      }, debounceTime)
    );

    // Make sure to call this to set the initial state
    this._resize();
    this._moveParallax();

    // Create the intersection observer
    const parallaxObserver = new IntersectionObserver((entries, observer) =>
      this._viewportHandler(entries, observer)
    );

    // Start observing
    parallaxObserver.observe(this._elements.image ? this._elements.image : this._elements.video);
  }

  /**
   * Determine if the parallax should be scrolling based
   * off the fact that it is inside of the viewport
   * @param {*} entries
   */
  _viewportHandler(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        window.addEventListener('scroll', this._moveParallax.bind(this));
      } else {
        window.removeEventListener('scroll', this._moveParallax.bind(this));
      }
    }
  }

  /**
   * Moving the parallax (works for both images and videos)
   */
  _moveParallax() {
    const scrolled = window.scrollY;

    const bodyRect = document.body.getBoundingClientRect();
    const elemRect = this._element.getBoundingClientRect();
    const offset = elemRect.top - bodyRect.top;

    let coords = `center calc(50% + ${(scrolled - offset) * this._options.speed}px)`;

    const minParallaxWidth = 500;

    if (this._state.width < minParallaxWidth) {
      coords = `center center`;
    }

    if (this._state.isVideo) {
      this._elements.video.style.top = `calc(50% + ${(scrolled - offset) * this._options.speed}px)`;
    } else {
      this._elements.image.style.backgroundPosition = coords;
    }
  }

  /**
   * Resize
   */
  _resize() {
    this._state.width =
      window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    // Set correct image based on the screen size (only for images, not videos)
    if (!this._state.isVideo) {
      const breakpointL = 1200;
      const breakpointXL = 1440;

      if (this._state.width > breakpointXL) {
        this._setImage('Xxl');
      } else if (this._state.width > breakpointL) {
        this._setImage('Xl');
      } else {
        this._setImage('M');
      }
    }
  }

  /**
   * Set the background image based on the screen size
   * @param {*} size
   */
  _setImage(size) {
    const image = this._elements.image.dataset[`image${size}`];
    this._elements.image.style.backgroundImage = `url('${image}')`;
    this._elements.image.style.backgroundSize = 'cover';
  }
}

export default Parallax;
